import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface EmailAvailabilityResult {
  status: 'available' | 'reserved_guest' | 'taken';
  message: string;
  can_signup: boolean;
  requires_activation?: boolean;
}

export function useEmailAvailability() {
  const [isChecking, setIsChecking] = useState(false);
  const [result, setResult] = useState<EmailAvailabilityResult | null>(null);

  const checkEmailAvailability = useCallback(async (email: string): Promise<EmailAvailabilityResult | null> => {
    if (!email) return null;
    
    setIsChecking(true);
    try {
      // Call edge function instead of RPC (works for unauthenticated users)
      const { data, error } = await supabase.functions.invoke(
        'check-email-before-signup',
        { body: { email } }
      );

      if (error) {
        console.error('Error checking email availability:', error);
        return null;
      }

      const availabilityResult: EmailAvailabilityResult = {
        status: data.status,
        message: data.message,
        can_signup: data.can_signup,
        requires_activation: data.requires_activation
      };

      setResult(availabilityResult);
      return availabilityResult;
    } catch (error) {
      console.error('Error checking email availability:', error);
      return null;
    } finally {
      setIsChecking(false);
    }
  }, []);

  const clearResult = useCallback(() => {
    setResult(null);
  }, []);

  return {
    checkEmailAvailability,
    isChecking,
    result,
    clearResult
  };
}